// client/src/realtime/events.ts
// Event types and payloads for real-time communication (mirrors backend)

export const RealtimeEventType = {
  // Conversation events
  CONVERSATION_MESSAGE_CREATED: 'conversation.message.created',
  CONVERSATION_UPDATED: 'conversation.updated',
  CONVERSATION_TYPING: 'conversation.typing',

  // Task events
  TASK_CREATED: 'task.created',
  TASK_UPDATED: 'task.updated',
  TASK_DELETED: 'task.deleted',

  // Task Checklist events
  TASK_CHECKLIST_ITEM_CREATED: 'task.checklist_item.created',
  TASK_CHECKLIST_ITEM_UPDATED: 'task.checklist_item.updated',
  TASK_CHECKLIST_ITEM_DELETED: 'task.checklist_item.deleted',
  TASK_CHECKLIST_ITEM_TOGGLED: 'task.checklist_item.toggled',

  // Task Template events
  TASK_TEMPLATE_CREATED: 'task.template.created',
  TASK_TEMPLATE_UPDATED: 'task.template.updated',
  TASK_TEMPLATE_DELETED: 'task.template.deleted',
  TASK_TEMPLATE_PAUSED: 'task.template.paused',
  TASK_TEMPLATE_RESUMED: 'task.template.resumed',

  // Opportunity events
  OPPORTUNITY_CREATED: 'opportunity.created',
  OPPORTUNITY_UPDATED: 'opportunity.updated',
  OPPORTUNITY_STAGE_CHANGED: 'opportunity.stage.changed',
  OPPORTUNITY_DELETED: 'opportunity.deleted',

  // Time tracking events
  TIME_ENTRY_CREATED: 'time.entry.created',
  TIME_ENTRY_UPDATED: 'time.entry.updated',
  TIME_ENTRY_DELETED: 'time.entry.deleted',

  // Event/calendar events
  EVENT_CREATED: 'event.created',
  EVENT_UPDATED: 'event.updated',
  EVENT_DELETED: 'event.deleted',

  // Contact events
  CONTACT_CREATED: 'contact.created',
  CONTACT_UPDATED: 'contact.updated',
  CONTACT_DELETED: 'contact.deleted',

  // Notification events
  NOTIFICATION_CREATED: 'notification.created',
  NOTIFICATION_READ: 'notification.read',

  // Access Events
  //
  // Pushed to ONE user when an administrator changes what they can use.
  // Must stay identical to `RealtimeEventType.ACCESS_CHANGED` on the server.
  ACCESS_CHANGED: 'access.changed',

  // Presence events
  PRESENCE_UPDATE: 'presence.update',
  USER_ONLINE: 'presence.user.online',
  USER_OFFLINE: 'presence.user.offline',

  // Assistant Action events (V2)
  ACTION_PLANNED: 'action.planned',
  ACTION_CONFIRMED: 'action.confirmed',
  ACTION_SKIPPED: 'action.skipped',
  ACTION_STATUS_CHANGED: 'action.status.changed',
  ACTION_COMPLETED: 'action.completed',
  ACTION_FAILED: 'action.failed',

  // Communication Thread/Message events
  THREAD_CREATED: 'thread.created',
  THREAD_UPDATED: 'thread.updated',
  MESSAGE_CREATED: 'message.created',
  MESSAGE_STATUS_CHANGED: 'message.status.changed',
  MESSAGE_READ: 'message.read',
  MESSAGE_REACTION_CHANGED: 'message.reaction.changed',
  THREAD_READ: 'thread.read',
  THREAD_PARTICIPANT_SEEN: 'thread.participant.seen',

  // Voicemail events
  VOICEMAIL_CREATED: 'voicemail.created',
  VOICEMAIL_STATUS_CHANGED: 'voicemail.status.changed',

  // Collaboration events
  COLLABORATION_SIGNAL: 'collaboration.signal',

  // Telephony — Incoming-call recording preference sync (per-Call SID,
  // per-user). Mirrors the same string constant on the server so a
  // typed subscribe on the client picks up the payload the server
  // sent via `emitToUser`. ADDITIVE.
  TELEPHONY_INCOMING_CALL_RECORDING_PREF_CHANGED:
    'telephony.incoming_call.recording_preference_changed',

  // Telephony — CallSession recording finished processing. Emitted by
  // the recording webhook after RecordingSid/Url land on CallSession.
  // Subscribers invalidate their CallSession list cache so the
  // Recordings tab flips "Processing" → "Ready" automatically. ADDITIVE.
  CALL_SESSION_RECORDING_READY: 'call_session.recording.ready',
  CALL_RECORDING_STARTED: 'call.recording.started',
  CALL_RECORDING_STOPPED: 'call.recording.stopped',
  CALL_RECORDING_COMPLETED: 'call.recording.completed',
  // ADDITIVE — CRM S3 ingestion lifecycle (server-emitted). Used by
  // the Recordings tab to flip the row badge Processing → Available
  // (or → Failed on terminal failure).
  CALL_RECORDING_INGESTION_STARTED: 'call.recording.ingestion.started',
  CALL_RECORDING_INGESTION_COMPLETED: 'call.recording.ingestion.completed',
  CALL_RECORDING_INGESTION_FAILED: 'call.recording.ingestion.failed',
  // Provider Mirror Events (Phase 4 — provider sync only).
  // GENERIC event fired after any successful mirror upsert/delete.
  // Client invalidates the Integration LIST RTK tag so Providers
  // page + pre-call selector update immediately. Payload contains
  // only ids + change kind — NEVER credentials/tokens/secrets.
  // Provider synchronization is metadata-only; this event does NOT
  // indicate a call or recording was created.
  PROVIDER_MIRROR_UPDATED: 'provider.mirror.updated',

  // Phase 12.4 — Circle ↔ Outbound Provider affinity events.
  // Emitted per workspace whenever a Head-authorized affinity mutation
  // succeeds on a Circle, or when the outbound-provider-deleted cascade
  // removes affinity rows. Client invalidates the effective-provider
  // list tag on the affected Circle so an open Circle Providers tab
  // refetches from the safe read endpoint. Payload is metadata-only —
  // workspaceId + circleId + outboundProviderId + changeKind — and
  // NEVER carries credentials or Outbound-side secrets.
  PROVIDER_AFFINITY_UPDATED: 'provider.affinity.updated',

  // Phase 12.7 — Circle → People per-member provider pin events.
  // Emitted after a Head-authorized add/update/remove on a member's
  // per-channel provider pin. Consumers invalidate the affected
  // Circle's members-provider tag. Payload is metadata-only —
  // workspaceId + circleId + userId + channelType + workspaceProviderId
  // + changeKind — and NEVER carries credentials or vendor secrets.
  PROVIDER_USER_ASSIGNMENT_UPDATED: 'provider.user_assignment.updated',

  // Circle Events — CRM-owned grouping primitive. Emitted per workspace
  // whenever a Circle is created/updated/archived/restored/deleted or
  // when its membership changes. Consumers invalidate `Circle` cache tags
  // so list + detail views refresh without a manual refetch.
  CIRCLE_CREATED: 'circle.created',
  CIRCLE_UPDATED: 'circle.updated',
  CIRCLE_ARCHIVED: 'circle.archived',
  CIRCLE_RESTORED: 'circle.restored',
  CIRCLE_DELETED: 'circle.deleted',
  CIRCLE_MEMBER_ADDED: 'circle.member.added',
  CIRCLE_MEMBER_REMOVED: 'circle.member.removed',

  // Provider Assignment events — Person↔Provider↔Department relationship.
  // Payloads carry only ids + minimal identifiers so consumers can pick
  // the right RTK tags to invalidate; the full row is re-fetched.
  PROVIDER_ASSIGNMENT_CREATED: 'provider.assignment.created',
  PROVIDER_ASSIGNMENT_UPDATED: 'provider.assignment.updated',
  PROVIDER_ASSIGNMENT_REASSIGNED: 'provider.assignment.reassigned',
  PROVIDER_ASSIGNMENT_ENDED: 'provider.assignment.ended',
  PROVIDER_ASSIGNMENT_ROLE_CHANGED: 'provider.assignment.role_changed',
  PROVIDER_ASSIGNMENT_PRIORITY_CHANGED: 'provider.assignment.priority_changed',
  PROVIDER_ASSIGNMENT_STATUS_CHANGED: 'provider.assignment.status_changed',

  // Compliance events. Emitted workspace-wide so dashboards
  // (Overview, Contact/Provider/Circle compliance sections,
  // Expiring list, Approval inbox) refresh without a manual
  // fetch. The consumer refetches via RTK Query tags.
  COMPLIANCE_REQUIREMENT_CREATED: 'compliance.requirement.created',
  COMPLIANCE_REQUIREMENT_UPDATED: 'compliance.requirement.updated',
  COMPLIANCE_ASSIGNMENT_CREATED: 'compliance.assignment.created',
  COMPLIANCE_ASSIGNMENT_UPDATED: 'compliance.assignment.updated',
  COMPLIANCE_DOCUMENT_CREATED: 'compliance.document.created',
  COMPLIANCE_DOCUMENT_UPDATED: 'compliance.document.updated',
  COMPLIANCE_APPROVAL_REQUESTED: 'compliance.approval.requested',
  COMPLIANCE_APPROVED: 'compliance.approved',
  COMPLIANCE_REJECTED: 'compliance.rejected',
  COMPLIANCE_EXPIRING: 'compliance.expiring',
  COMPLIANCE_EXPIRED: 'compliance.expired',
  COMPLIANCE_RENEWED: 'compliance.renewed',
} as const;

// Realtime payload for CIRCLE_* events. Server-emitted payloads carry
// only the ids + minimal metadata needed to reason about the change; the
// consumer refetches the full row through RTK Query.
export interface CircleRealtimePayload {
  circleId: string;
  name?: string;
  type?: string;
  memberId?: string;
  removedCount?: number;
  purged?: boolean;
}

// Payload for PROVIDER_ASSIGNMENT_* events. The service always includes
// enough context for the FE to pick which tags to invalidate — most
// events carry the assignmentId; reassign carries both previous and new.
export interface ProviderAssignmentRealtimePayload {
  assignmentId?: string;
  previousAssignmentId?: string;
  newAssignmentId?: string;
  contactId?: string;
  providerUserId?: string;
  departmentCircleId?: string;
  priority?: string;
  status?: string;
  finalStatus?: string;
  newProviderRoleId?: string;
  newPriority?: string;
  newStatus?: string;
}

export type RealtimeEventTypeValue = typeof RealtimeEventType[keyof typeof RealtimeEventType];

// Client to server message types (must match server's ClientMessageType)
export const ClientMessageType = {
  SUBSCRIBE_WORKSPACE: 'subscribe.workspace',
  UNSUBSCRIBE_WORKSPACE: 'unsubscribe.workspace',
  SUBSCRIBE_CONVERSATION: 'subscribe.conversation',
  UNSUBSCRIBE_CONVERSATION: 'unsubscribe.conversation',
  TYPING_START: 'typing.start',
  TYPING_STOP: 'typing.stop',
  PING: 'ping',
} as const;

export type ClientMessageTypeValue = typeof ClientMessageType[keyof typeof ClientMessageType];

// Payload interfaces
export interface ConversationMessagePayload {
  conversationId: string;
  workspaceId: string;
  message: {
    id: string;
    sessionId: string;
    senderType: string;
    senderUserId: string | null;
    content: string;
    createdAt: string;
  };
}

export interface ConversationTypingPayload {
  conversationId: string;
  workspaceId: string;
  userId: string;
  userFullName: string;
  isTyping: boolean;
}

export interface TaskPayload {
  taskId: string;
  workspaceId: string;
  task: {
    id: string;
    workspaceId: string;
    title: string;
    description: string | null;
    status: string;
    priority: string;
    dueDate: string | null;
    assigneeUserId: string | null;
    createdByUserId: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface TaskDeletedPayload {
  taskId: string;
  workspaceId: string;
}

export interface OpportunityPayload {
  opportunityId: string;
  workspaceId: string;
  pipelineId: string;
  previousStageId?: string;
  opportunity: {
    id: string;
    workspaceId: string;
    pipelineId: string;
    stageId: string;
    title: string;
    amount: number | null;
    currency: string;
    status: string;
    expectedCloseDate: string | null;
    contactId: string | null;
    assignedToUserId: string | null;
    createdAt: string;
    updatedAt: string;
  };
}

export interface OpportunityDeletedPayload {
  opportunityId: string;
  workspaceId: string;
  pipelineId: string;
}

export interface TimeEntryPayload {
  timeEntryId: string;
  workspaceId: string;
  userId: string;
  timeEntry: {
    id: string;
    workspaceId: string;
    userId: string;
    clockInAt: string;
    clockOutAt: string | null;
    breakMinutes: number;
    status: string;
    notes: string | null;
    createdAt: string;
    updatedAt: string;
  };
}

export interface TimeEntryDeletedPayload {
  timeEntryId: string;
  workspaceId: string;
  userId: string;
}

export interface PresenceUpdatePayload {
  workspaceId: string;
  users: Array<{
    id: string;
    fullName: string;
    status: 'online' | 'away' | 'offline';
  }>;
}

export interface UserOnlinePayload {
  workspaceId: string;
  userId: string;
  fullName: string;
}

export interface UserOfflinePayload {
  workspaceId: string;
  userId: string;
}

// ============================================================
// Assistant Action Payloads (V2)
// ============================================================

export interface ActionPayloadAction {
  id: string;
  conversationId: string;
  workspaceId: string;
  actionType: string;
  status: string;
  targetType: string | null;
  targetId: string | null;
  summary: string;
  payload: Record<string, unknown>;
  errorMessage: string | null;
  suggestedBy: string;
  requiresConfirmation: boolean;
  explanation?: string;
  confidence?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ActionPayload {
  actionId: string;
  conversationId: string;
  workspaceId: string;
  action: ActionPayloadAction;
  previousStatus?: string;
}

export interface ActionPlannedPayload extends ActionPayload {
  reasoning?: string;
}

// Server to client event shape
export interface RealtimeEvent<T = unknown> {
  type: RealtimeEventTypeValue;
  payload: T;
  timestamp: string;
}

// Client to server message shape (matches server's ClientMessage union type)
export type ClientMessage =
  | { type: typeof ClientMessageType.SUBSCRIBE_WORKSPACE; workspaceId: string }
  | { type: typeof ClientMessageType.UNSUBSCRIBE_WORKSPACE; workspaceId: string }
  | { type: typeof ClientMessageType.SUBSCRIBE_CONVERSATION; conversationId: string }
  | { type: typeof ClientMessageType.UNSUBSCRIBE_CONVERSATION; conversationId: string }
  | { type: typeof ClientMessageType.TYPING_START; conversationId: string }
  | { type: typeof ClientMessageType.TYPING_STOP; conversationId: string }
  | { type: typeof ClientMessageType.PING };

// System messages from server
export interface SystemMessage {
  type: 'connected' | 'subscribed' | 'unsubscribed' | 'error' | 'pong';
  connectionId?: string;
  topic?: string;
  message?: string;
  presence?: Array<{ id: string; fullName: string }>;
}

// ============================================================
// Communication Message Payloads
// ============================================================

/**
 * Sender info included in thread payloads for instant cache updates
 */
export interface ThreadSenderInfo {
  id: string;
  fullName: string | null;
  email: string | null;
}

/**
 * Contact info included in thread payloads for instant cache updates
 */
export interface ThreadContactInfo {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
}

/**
 * Sender info included in message payloads for instant cache updates
 */
export interface MessageSenderInfo {
  id: string;
  fullName: string | null;
  email: string | null;
  avatarUrl?: string | null;
}

export interface ThreadPayload {
  threadId: string;
  workspaceId: string;
  thread: {
    id: string;
    workspaceId: string;
    primaryMedium: string | null;
    source?: string | null;
    externalAddress: string | null;
    subject: string | null;
    lastMessageAt: string | null;
    lastMessagePreview: string | null;
    lastMessageMedium: string | null;
    unreadCount: number;
    isArchived: boolean;
    primaryContactId: string | null;
    createdByUserId?: string | null;
    createdAt: string;
    updatedAt: string;
    // Enriched fields for instant cache update (no refetch needed)
    sender?: ThreadSenderInfo | null;
    contact?: ThreadContactInfo | null;
  };
}

export interface MessagePayload {
  messageId: string;
  threadId: string;
  workspaceId: string;
  message: {
    id: string;
    threadId: string;
    workspaceId: string;
    medium: string;
    direction: string;
    status: string;
    fromAddress: string | null;
    toAddress: string | null;
    subject: string | null;
    bodyText: string | null;
    bodyHtml: string | null;
    attachments: unknown[] | null;
    externalMessageId: string | null;
    errorMessage: string | null;
    sentAt: string | null;
    deliveredAt: string | null;
    readAt: string | null;
    contactId: string | null;
    senderUserId: string | null;
    createdAt: string;
    updatedAt: string;
    // Enriched field for instant cache update (no refetch needed)
    sender?: MessageSenderInfo | null;
  };
  // Thread summary update info (for updating thread list instantly)
  threadUpdate?: {
    lastMessageAt: string;
    lastMessagePreview: string;
    lastMessageMedium: string;
  };
}

export interface MessageStatusPayload {
  messageId: string;
  threadId: string;
  workspaceId: string;
  status: string;
  previousStatus: string;
  errorMessage?: string | null;
  deliveredAt?: string | null;
  readAt?: string | null;
}

export interface ThreadReadPayload {
  threadId: string;
  workspaceId: string;
  unreadCount: number;
}

export interface ThreadParticipantSeenPayload {
  threadId: string;
  workspaceId: string;
  userId: string;
  userFullName: string;
  lastSeenAt: string;
}

// ============================================================
// Collaboration Signal Payloads
// ============================================================

export type CollaborationSignalType = 'typing' | 'on_call' | 'listening_voicemail' | 'idle';

export interface CollaborationSignalPayload {
  threadId: string;
  workspaceId: string;
  userId: string;
  userFullName: string;
  signalType: CollaborationSignalType;
  metadata?: Record<string, unknown>;
  expiresAt?: string;
}

// ============================================================
// Message Reaction Payloads
// ============================================================

export interface ReactionSummary {
  emoji: string;
  count: number;
  userIds: string[];
}

export interface MessageReactionPayload {
  messageId: string;
  threadId: string;
  workspaceId: string;
  action: 'added' | 'removed';
  emoji: string;
  userId: string;
  userFullName: string | null;
  reactions: ReactionSummary[];
}

// ============================================================
// Task Checklist Payloads
// ============================================================

export interface TaskChecklistItemPayload {
  checklistItemId: string;
  taskId: string;
  workspaceId: string;
  item: {
    id: string;
    taskId: string;
    title: string;
    completed: boolean;
    completedAt: string | null;
    completedByUserId: string | null;
    position: number;
    createdAt: string;
    updatedAt: string;
  };
}

export interface TaskChecklistItemDeletedPayload {
  checklistItemId: string;
  taskId: string;
  workspaceId: string;
}

export interface TaskChecklistItemToggledPayload {
  checklistItemId: string;
  taskId: string;
  workspaceId: string;
  completed: boolean;
  completedAt: string | null;
  completedByUserId: string | null;
  position: number;
}

// ============================================================
// Task Template Payloads
// ============================================================

export interface RecurrenceRule {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  monthOfYear?: number;
  endDate?: string;
  count?: number;
}

export interface TaskTemplatePayload {
  templateId: string;
  workspaceId: string;
  template: {
    id: string;
    workspaceId: string;
    title: string;
    description: string | null;
    priority: string;
    assigneeUserIds: string[] | null;
    relatedContactId: string | null;
    goalId: string | null;
    tags: string[] | null;
    recurrenceRule: RecurrenceRule;
    startAt: string;
    timezone: string;
    isActive: boolean;
    lastGeneratedAt: string | null;
    nextOccurrenceAt: string | null;
    createdByUserId: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface TaskTemplateDeletedPayload {
  templateId: string;
  workspaceId: string;
}

/**
 * Payload for PROVIDER_MIRROR_UPDATED (Phase 4). Metadata-only.
 * NEVER carries credentials, tokens, phone secrets, Twilio secrets,
 * or arbitrary provider settings — client reacts by invalidating the
 * Integration LIST RTK tag and re-fetching the safe GET
 * /api/integrations endpoint (already-filtered payload). Emitted
 * only when a mirror upsert or delete actually mutates state
 * (rejected outcomes emit nothing).
 */
export interface ProviderMirrorUpdatedPayload {
  workspaceId: string;
  appKey: string;
  integrationId: string;
  changeKind: 'upsert' | 'delete';
  syncedAt: string;
}
